/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { messages, type MessageKey } from "./messages";
import { resolveLocale, type Locale } from "./locale";

interface I18nValue { locale: Locale; t: (key: MessageKey) => string; setLocale: (locale: Locale) => Promise<void>; saving: boolean; error: string | null }
const I18nContext = createContext<I18nValue | null>(null);
const STORAGE_KEY = "kitsos.locale";
const PENDING_KEY = "kitsos.pendingLocale";

export function I18nProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const pending = sessionStorage.getItem(PENDING_KEY);
  const stored = localStorage.getItem(STORAGE_KEY);
  const [locale, setLocaleState] = useState<Locale>(() => resolveLocale({ pending, claim: auth.sessionClaims?.locale, stored }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { document.documentElement.lang = locale; localStorage.setItem(STORAGE_KEY, locale); }, [locale]);
  useEffect(() => { const claimed = auth.sessionClaims?.locale; if (claimed === locale) sessionStorage.removeItem(PENDING_KEY); }, [auth.sessionClaims, locale]);
  const setLocale = useCallback(async (next: Locale) => {
    setSaving(true); setError(null); setLocaleState(next);
    localStorage.setItem(STORAGE_KEY, next); sessionStorage.removeItem(PENDING_KEY);
    setSaving(false);
  }, []);
  const value = useMemo<I18nValue>(() => ({ locale, t: key => messages[locale][key] ?? messages.en[key], setLocale, saving, error }), [locale, setLocale, saving, error]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
export function useI18n() { const value = useContext(I18nContext); if (!value) throw new Error("I18nProvider missing"); return value; }
export type { Locale } from "./locale";
